import { faPenCircle, faUserSlash } from "@fortawesome/pro-solid-svg-icons"
import { Button, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { dateSVC } from "app/util/date/service"
import { messageSVC } from "message/service"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { ListIdCell } from "ui/components/ListIdCell"
import { ListActionButton } from "ui/components/main-layout/ListActionButton"
import { getDataGridTexts } from "ui/tools/list"
import { IUserImpersonatorHydrated } from "user/model"
import { getUserDisplayName } from "user/tools/common"
import { deleteImpersonator, getGivenImpersonators } from "user/tools/impersonate"
import { ImpersonatorFormPopup } from "./ImpersonatorFormPopup"

export const GivenImpersonators = () => {
  const [givenImpersonators, setGivenImpersonators] = React.useState<IUserImpersonatorHydrated[] | null>(null)
  const [hasLoadingError, setHasLoadingError] = React.useState(false)
  const [isPopupOpened, setIsPopupOpened] = React.useState(false)
  const [selectedImpersonator, setSelectedImpersonator] = React.useState<IUserImpersonatorHydrated | null>(null)
  React.useEffect(() => {
    getGivenImpersonators()
      .then(impersonators => setGivenImpersonators(impersonators))
      .catch(error => {
        console.error("Unable to fetch given impersonators", error)
        messageSVC.error(translate("user.impersonate.given.loading.error"))
        setHasLoadingError(true)
      })
  }, [])
  const openEditPopup = (user: IUserImpersonatorHydrated) => {
    setSelectedImpersonator(user)
    setIsPopupOpened(true)
  }
  const columns: Array<GridColDef<IUserImpersonatorHydrated>> = [
    {
      field: "email",
      headerName: translate("label.user"),
      flex: 4,
      renderCell: params => (
        <ListIdCell
          cellId={`given-impersonator-id-${params.row.id}`}
          id={params.row.impersonator_id}
          label={getUserDisplayName(params.row)}
        />
      )
    },
    {
      field: "start_date",
      headerName: translate("label.startDate"),
      flex: 2,
      renderCell: params => (params.row.start_date ? dateSVC.format(params.row.start_date) : "")
    },
    {
      field: "end_date",
      headerName: translate("label.endDate"),
      flex: 2,
      renderCell: params => (params.row.end_date ? dateSVC.format(params.row.end_date) : "")
    },
    {
      field: "actions",
      headerName: translate("label.actions"),
      flex: 2,
      renderCell: params => (
        <FlexBox gap>
          <ListActionButton
            id={`user-impersonator-change-${params.row.email}`}
            icon={faPenCircle}
            tooltip={translate("label.change")}
            onClick={() => openEditPopup(params.row)}
          />
          <ListActionButton
            id={`user-impersonator-delete-${params.row.email}`}
            icon={faUserSlash}
            tooltip={translate("user.impersonate.remove")}
            onClick={() => {
              deleteImpersonator(params.row.impersonator_id).then(isSuccess => {
                if (isSuccess && givenImpersonators) {
                  setGivenImpersonators(
                    givenImpersonators.filter(i => i.impersonator_id !== params.row.impersonator_id)
                  )
                }
              })
            }}
          />
        </FlexBox>
      )
    }
  ]
  const isEmpty = !givenImpersonators || givenImpersonators.length === 0
  return (
    <FlexBox id="given-impersonate-users" vertical fullWidth ai="start">
      {hasLoadingError ? (
        <Typography color="error" fontSize=".9rem">
          {translate("user.impersonate.given.loading.error")}
        </Typography>
      ) : givenImpersonators === null ? (
        <LinearLoader />
      ) : (
        <FlexBox
          fullWidth
          vertical
          gap
          id="given-impersonate-users-list-container"
          sx={{
            "position": "relative",
            "height": isEmpty ? undefined : "20rem",
            "minHeight": isEmpty ? undefined : "20rem",
            "maxHeight": isEmpty ? undefined : "20rem",
            "transition": "all 400ms ease",
            "& > *": {
              width: "100%"
            },
            "& .MuiDataGrid-selectedRowCount": {
              visibility: "hidden"
            },
            "& .MuiDataGrid-root": {
              border: "none"
            },
            "& .MuiDataGrid-cell": {
              fontSize: ".8rem",
              fontWeight: 300
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer"
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "inherit"
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
              {
                outline: "none !important"
              }
          }}
        >
          <FlexBox jc="space-between">
            <Typography color="textPrimary" fontSize=".9rem" fontWeight={500}>
              {translate("users.impersonators.given")}
            </Typography>
            <Button
              id="given-impersonate-users-add-button"
              variant="outlined"
              onClick={() => {
                if (selectedImpersonator) {
                  setSelectedImpersonator(null)
                }
                setIsPopupOpened(true)
              }}
            >
              {translate("button.add")}
            </Button>
          </FlexBox>
          {givenImpersonators.length === 0 ? (
            <Typography id="given-impersonate-users-empty" color="textSecondary" fontSize=".85rem" sx={{ pl: ".5rem" }}>
              {translate("user.impersonator.given.empty")}
            </Typography>
          ) : (
            <DataGrid
              rows={givenImpersonators}
              columns={columns}
              className="given-impersonators"
              rowHeight={40}
              autoPageSize
              onRowClick={params => openEditPopup(params.row)}
              disableSelectionOnClick
              localeText={getDataGridTexts()}
            />
          )}
          {isPopupOpened && (
            <ImpersonatorFormPopup
              existingImpersonators={givenImpersonators}
              onClose={() => setIsPopupOpened(false)}
              onSave={newImpersonator => {
                if (selectedImpersonator) {
                  setGivenImpersonators(
                    givenImpersonators.map(i => (i.id === newImpersonator.id ? newImpersonator : i))
                  )
                  setSelectedImpersonator(null)
                } else {
                  setGivenImpersonators([...givenImpersonators, newImpersonator])
                }
                setIsPopupOpened(false)
              }}
              impersonator={selectedImpersonator}
            />
          )}
        </FlexBox>
      )}
    </FlexBox>
  )
}
